import React from "react";
import { VisuallyHidden } from "react-aria";
import type { TextFieldProps } from "react-aria-components";
import {
    FieldError,
    Input,
    Label,
    TextField as ReactAriaTextField,
    Text,
    TextArea,
} from "react-aria-components";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

export interface InputProps {
    /**
     * For accessibility benefits, there should always be a label, but it
     * doesn't have to be visible to all.
     */
    labelVisible?: boolean;
    label: string;
    description?: string;
    placeholder?: string;
}

interface Props extends InputProps, TextFieldProps {
    textArea?: boolean;
}

export const TextField = ({
    label,
    labelVisible = true,
    description,
    textArea = false,
    ...props
}: Props) => {
    return (
        <ReactAriaTextField {...props}>
            <Label
                className={concatClassNames([
                    labelVisible ? styles.label : undefined,
                    props.isRequired ? styles.required : undefined,
                ])}
            >
                {labelVisible ? (
                    label
                ) : (
                    <VisuallyHidden>{label}</VisuallyHidden>
                )}
            </Label>
            {textArea ? (
                <TextArea className={styles.textInput} />
            ) : (
                <Input className={styles.textInput} />
            )}
            {description && (
                <Text className={styles.description} slot="description">
                    {description}
                </Text>
            )}
            <FieldError className={styles.errorMessage}>
                {({ validationDetails, validationErrors }) =>
                    validationDetails?.valueMissing
                        ? t`This field is required.`
                        : validationErrors
                }
            </FieldError>
        </ReactAriaTextField>
    );
};
